import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * PasswordField — a password input with a show/hide (eye) toggle for what the person
 * TYPES, which never reveals a SAVED password (NEXUS-586 follow-up).
 *
 * A value counts as typed only when it arrived through user `input` events
 * (insertText, insertFromPaste, insertReplacementText, deleteContent…, …). Anything else
 * — an `input` event with no / an unknown inputType (browser autofill), a value the
 * page set without a user input event, a value already there on mount, or the
 * `:-webkit-autofill` / `:autofill` animation hook — marks the field AUTOFILLED: the
 * toggle is disabled ("Saved passwords can't be shown") until the field is cleared
 * completely; typing after that re-enables it. Editing an autofilled value without
 * clearing it keeps it disabled.
 *
 * A reveal hides itself after 30 s, when focus leaves the form, and on submit. The
 * value is never logged. Every other input prop (autoComplete, id, name, value,
 * onChange, …) is passed through.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, } from 'react';
export const REVEAL_TIMEOUT_MS = 30_000;
export const AUTOFILL_ANIMATION = 'nx-pwfield-autofill';
export const SAVED_PASSWORD_TOOLTIP = "Saved passwords can't be shown";
/** inputType values that mean a person edited the field. */
const USER_INPUT_TYPES = new Set([
    'insertText', 'insertFromPaste', 'insertReplacementText', 'insertCompositionText', 'insertFromDrop',
    'insertLineBreak', 'deleteContentBackward', 'deleteContentForward', 'deleteByCut', 'deleteContent',
    'deleteWordBackward', 'deleteWordForward', 'deleteSoftLineBackward', 'deleteSoftLineForward',
    'deleteHardLineBackward', 'deleteHardLineForward', 'deleteEntireSoftLine', 'historyUndo', 'historyRedo',
]);
/** One stylesheet for the autofill hook (Chrome/Edge/Safari :-webkit-autofill, Firefox :autofill). */
function ensureAutofillStyle(doc) {
    if (doc.getElementById('nx-pwfield-style'))
        return;
    const style = doc.createElement('style');
    style.id = 'nx-pwfield-style';
    style.textContent =
        `@keyframes ${AUTOFILL_ANIMATION} { from { opacity: 1; } to { opacity: 1; } }\n` +
            `input.nx-pwfield:-webkit-autofill { animation-name: ${AUTOFILL_ANIMATION}; animation-duration: 1ms; }\n` +
            `input.nx-pwfield:autofill { animation-name: ${AUTOFILL_ANIMATION}; animation-duration: 1ms; }`;
    doc.head.appendChild(style);
}
export const PasswordField = forwardRef(function PasswordField({ groupClassName, append, revealTimeoutMs = REVEAL_TIMEOUT_MS, className, value, disabled, ...rest }, forwarded) {
    const ref = useRef(null);
    useImperativeHandle(forwarded, () => ref.current);
    const [revealed, setRevealed] = useState(false);
    const [autofilled, setAutofilled] = useState(false);
    /** Value seen by the last native input event — a `value` prop that differs came from elsewhere. */
    const lastEventValue = useRef(null);
    const timer = useRef(null);
    const hide = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
        setRevealed(false);
    }, []);
    const markAutofilled = useCallback(() => { setAutofilled(true); hide(); }, [hide]);
    // Native listeners: React's synthetic events do not expose inputType reliably.
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        ensureAutofillStyle(el.ownerDocument);
        // Already filled when it appeared (restored / prefilled / autofilled before mount).
        if (el.value !== '')
            markAutofilled();
        const onInput = (e) => {
            const inputType = e.inputType;
            lastEventValue.current = el.value;
            if (el.value === '') {
                setAutofilled(false);
                return;
            }
            if (!inputType || !USER_INPUT_TYPES.has(inputType))
                markAutofilled();
            // A user edit of an autofilled value leaves it autofilled (only clearing resets it).
        };
        const onAnimation = (e) => {
            if (e.animationName === AUTOFILL_ANIMATION)
                markAutofilled();
        };
        const form = el.form;
        const onFocusOut = (e) => {
            const to = e.relatedTarget;
            if (!to || !(form ?? el.parentElement)?.contains(to))
                hide();
        };
        const scope = form ?? el.parentElement;
        el.addEventListener('input', onInput);
        el.addEventListener('animationstart', onAnimation);
        scope?.addEventListener('focusout', onFocusOut);
        form?.addEventListener('submit', hide);
        return () => {
            el.removeEventListener('input', onInput);
            el.removeEventListener('animationstart', onAnimation);
            scope?.removeEventListener('focusout', onFocusOut);
            form?.removeEventListener('submit', hide);
        };
    }, [hide, markAutofilled]);
    // Controlled use: a value that did not come through an input event is not "typed".
    useLayoutEffect(() => {
        if (value === undefined)
            return;
        const v = String(value ?? '');
        if (v === '') {
            setAutofilled(false);
            lastEventValue.current = '';
            return;
        }
        if (lastEventValue.current !== v)
            markAutofilled();
    }, [value, markAutofilled]);
    useEffect(() => () => { if (timer.current)
        clearTimeout(timer.current); }, []);
    const toggle = () => {
        if (revealed) {
            hide();
            return;
        }
        if (autofilled)
            return;
        setRevealed(true);
        if (timer.current)
            clearTimeout(timer.current);
        timer.current = setTimeout(() => { timer.current = null; setRevealed(false); }, revealTimeoutMs);
    };
    const canReveal = !autofilled && !disabled;
    const label = revealed ? 'Hide password' : 'Show password';
    return (_jsxs("div", { className: `input-group${groupClassName ? ` ${groupClassName}` : ''}`, children: [_jsx("input", { ...rest, ref: ref, value: value, disabled: disabled, type: revealed && !autofilled ? 'text' : 'password', className: `${className ?? 'form-control'} nx-pwfield` }), _jsx("button", { type: "button", className: "btn btn-outline-secondary", "aria-label": label, "aria-pressed": revealed, title: autofilled ? SAVED_PASSWORD_TOOLTIP : label, disabled: !canReveal, onClick: toggle, children: revealed ? (_jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", fill: "currentColor", viewBox: "0 0 16 16", "aria-hidden": "true", children: [_jsx("path", { d: "M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7 7 0 0 0-2.79.588l.77.771A6 6 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755q-.247.248-.517.486z" }), _jsx("path", { d: "M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829" }), _jsx("path", { d: "M3.35 5.47q-.27.24-.518.487A13 13 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7 7 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12z" })] })) : (_jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", fill: "currentColor", viewBox: "0 0 16 16", "aria-hidden": "true", children: [_jsx("path", { d: "M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" }), _jsx("path", { d: "M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" })] })) }), append] }));
});
//# sourceMappingURL=PasswordField.js.map